<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">訂位參數設定</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleBack">
          <i class="ri-arrow-left-line"></i>
          返回
        </button>
        <button
          v-if="activeTab !== 'closures'"
          type="button"
          class="btn btn-primary"
          :disabled="submitting || !selectedStoreId"
          @click="handleSubmit"
        >
          <span v-if="submitting">儲存中...</span>
          <span v-else>儲存設定</span>
        </button>
      </div>
    </div>

    <div class="page-container">
    <div class="reservation-store-settings-container">

    <div class="form-container">
      <div class="form-section">
        <h3 class="section-title">顧客訂位連結</h3>
        <p class="form-hint">
          全門市連結讓顧客自己選店。選擇門市後，會再顯示該店的訂位連結。LINE 連結給官方帳號選單使用；一般瀏覽器連結不用登入 LINE，由顧客自己填資料。
        </p>

        <div v-if="bookingLinksLoading" class="form-hint">載入連結中...</div>
        <div v-else-if="bookingLinkRows.length === 0" class="form-hint">
          尚未設定 LINE 官方帳號，請先到 LINE 設定建立。
        </div>
        <div v-else class="booking-link-list">
          <div v-for="link in bookingLinkRows" :key="link.key" class="booking-link-row">
            <div class="booking-link-label">{{ link.label }}</div>
            <div class="booking-link-value">
              <a
                v-if="link.url"
                :href="link.url"
                target="_blank"
                rel="noopener noreferrer"
              >{{ link.url }}</a>
              <span v-else class="booking-link-missing">{{ link.missing }}</span>
            </div>
            <button
              v-if="link.url"
              type="button"
              class="btn btn-secondary btn-sm"
              @click="copyBookingLink(link)"
            >
              {{ copiedLinkKey === link.key ? '已複製' : '複製' }}
            </button>
          </div>
          <p v-if="selectedStore && !form.bookingEnabled" class="form-hint">
            此門市尚未啟用預約，顧客打開連結時不會看到這間店。
          </p>
        </div>
      </div>

      <div class="form-section">
        <h3 class="section-title">店家候位連結</h3>
        <p class="form-hint">
          選擇門市後，可複製這間店的 LINE 候位連結。顧客從 LINE 開啟後直接登記這間店。
        </p>
        <div v-if="bookingLinksLoading" class="form-hint">載入連結中...</div>
        <div v-else-if="!selectedStore" class="form-hint">請先選擇門市。</div>
        <div v-else-if="!storeWaitlistUrl" class="form-hint">
          請先到 LINE 設定填寫共用入口 LIFF ID。
        </div>
        <div v-else class="booking-link-list">
          <div class="booking-link-row">
            <div class="booking-link-label">{{ selectedStore.storeName }}</div>
            <div class="booking-link-value">
              <a
                :href="storeWaitlistUrl"
                target="_blank"
                rel="noopener noreferrer"
              >{{ storeWaitlistUrl }}</a>
            </div>
            <button
              type="button"
              class="btn btn-secondary btn-sm"
              @click="copyBookingLink({ key: 'store-waitlist', url: storeWaitlistUrl })"
            >
              {{ copiedLinkKey === 'store-waitlist' ? '已複製' : '複製' }}
            </button>
          </div>
        </div>
      </div>

      <div class="form-section">
        <h3 class="section-title">Step 1：選擇門市</h3>

        <div class="form-group">
          <label class="required">門市</label>
          <div
            v-if="selectedStoreId && selectedStore && !isSelectingStore"
            class="selected-store-compact"
          >
            <div
              class="selected-store-card"
              :class="{ 'is-booking-enabled': selectedStore.bookingEnabled }"
            >
              <div class="selected-store-image-wrapper">
                <img
                  v-if="getStoreImage(selectedStore)"
                  :src="getStoreImage(selectedStore)"
                  :alt="selectedStore.storeName"
                  class="selected-store-image"
                />
                <div v-else class="selected-store-image-placeholder">
                  <i class="ri-store-2-line"></i>
                </div>
              </div>
              <div class="selected-store-content">
                <div class="selected-store-name-row">
                  <div class="selected-store-name">{{ selectedStore.storeName }}</div>
                  <span v-if="selectedStore.bookingEnabled" class="booking-badge">已開啟預約</span>
                </div>
                <div class="selected-store-address">{{ formatStoreAddress(selectedStore) }}</div>
              </div>
            </div>
            <button type="button" class="btn btn-secondary btn-sm" @click="startStoreSelection">
              更換門市
            </button>
          </div>
          <div v-else class="store-picker" :class="{ 'is-invalid': errors.storeId }">
            <button
              v-for="store in stores"
              :key="store.id"
              type="button"
              class="store-picker-btn"
              :class="{
                'is-selected': selectedStoreId === store.id,
                'is-booking-enabled': store.bookingEnabled
              }"
              @click="selectStore(store.id)"
            >
              <div class="store-card-image-wrapper">
                <img
                  v-if="getStoreImage(store)"
                  :src="getStoreImage(store)"
                  :alt="store.storeName"
                  class="store-card-image"
                />
                <div v-else class="store-card-image-placeholder">
                  <i class="ri-store-2-line"></i>
                </div>
              </div>
              <div class="store-card-content">
                <div class="store-card-name-row">
                  <div class="store-card-name">{{ store.storeName }}</div>
                  <span v-if="store.bookingEnabled" class="booking-badge">已開啟預約</span>
                </div>
                <div class="store-card-address">{{ formatStoreAddress(store) }}</div>
              </div>
            </button>
          </div>
          <div v-if="errors.storeId" class="invalid-feedback">
            {{ errors.storeId }}
          </div>
          <p class="form-hint">綠色卡片是已開啟預約的門市。選擇門市後可獨立管理該門市的訂位參數。</p>
        </div>
      </div>

      <div v-if="selectedStoreId" class="tab-panel">
        <div class="tabs-nav" role="tablist" aria-label="訂位參數、營業時間與店休日">
          <button
            type="button"
            class="tabs-nav-item"
            :class="{ active: activeTab === 'settings' }"
            @click="activeTab = 'settings'"
          >
            訂位參數
          </button>
          <button
            type="button"
            class="tabs-nav-item"
            :class="{ active: activeTab === 'hours' }"
            @click="activeTab = 'hours'"
          >
            營業時間
          </button>
          <button
            type="button"
            class="tabs-nav-item"
            :class="{ active: activeTab === 'closures' }"
            @click="openClosuresTab"
          >
            店休日
          </button>
        </div>

        <div v-if="activeTab === 'settings'" class="form-section">
          <h3 class="section-title">Step 2：設定訂位參數</h3>

        <label class="booking-enable-card" :class="{ 'is-on': form.bookingEnabled }">
          <span class="booking-enable-switch">
            <input
              v-model="form.bookingEnabled"
              type="checkbox"
              aria-label="啟用預約服務"
            />
            <span class="booking-enable-slider" aria-hidden="true"></span>
          </span>
          <span class="booking-enable-copy">
            <span class="booking-enable-title">
              啟用預約服務
              <span class="booking-enable-state">{{ form.bookingEnabled ? '開啟' : '關閉' }}</span>
            </span>
            <span class="booking-enable-status">
              {{ form.bookingEnabled ? '已開啟，顧客可以看到這間店並完成訂位' : '尚未開啟，顧客打開連結時不會看到這間店' }}
            </span>
          </span>
        </label>

        <template v-if="form.bookingEnabled">
          <div class="form-row">
            <div class="form-group">
              <label for="advanceBookingMode">提前預約模式</label>
              <select
                id="advanceBookingMode"
                v-model="form.advanceBookingMode"
                class="form-control"
                @change="handleAdvanceBookingModeChange"
              >
                <option value="hours">提前多少小時</option>
                <option value="days">提前多少天</option>
              </select>
              <p class="form-hint">切換為提前天數模式時，預設值會帶入 1 天。</p>
            </div>

            <div v-if="form.advanceBookingMode === 'hours'" class="form-group">
              <label for="minAdvanceHours">最少提前時數</label>
              <input
                id="minAdvanceHours"
                v-model.number="form.minAdvanceHours"
                type="number"
                class="form-control"
                min="0"
              />
              <p class="form-hint">預約需提前的小時數。</p>
            </div>

            <div v-else class="form-group">
              <label for="advanceBookingDays">最少提前天數</label>
              <input
                id="advanceBookingDays"
                v-model.number="form.advanceBookingDays"
                type="number"
                class="form-control"
                min="1"
              />
              <p class="form-hint">預約需提前的天數，最少 1 天。</p>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="slotDuration">時段長度（分鐘）</label>
              <input
                id="slotDuration"
                v-model.number="form.slotDuration"
                type="number"
                class="form-control"
                min="0"
              />
              <p class="form-hint">可選時段的間隔，與用餐時間長度不同。</p>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="checkbox-label">
                <input
                  v-model="form.limitDiningDuration"
                  type="checkbox"
                  class="form-checkbox"
                />
                <span>限定用餐時間長度</span>
              </label>
              <p class="form-hint">
                開啟後，同一張桌子在用餐結束後可再被預約；關閉時，同一日同一桌僅能預約一次。
              </p>
            </div>
          </div>

          <div v-if="form.limitDiningDuration" class="form-row">
            <div class="form-group">
              <label for="diningDurationMinutes" class="required-label">用餐時間長度（分鐘）</label>
              <input
                id="diningDurationMinutes"
                v-model.number="form.diningDurationMinutes"
                type="number"
                class="form-control"
                :class="{ 'is-invalid': errors.diningDurationMinutes }"
                min="1"
                step="1"
              />
              <p class="form-hint">例如 90 分鐘：12:00 訂位占用至 13:30，13:30 起同一桌可再預約。</p>
              <div v-if="errors.diningDurationMinutes" class="invalid-feedback d-block">
                {{ errors.diningDurationMinutes }}
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="checkbox-label">
                <input
                  v-model="form.allowUpwardTableAssignment"
                  type="checkbox"
                  class="form-checkbox"
                />
                <span>人數少時允許向上相容改派大桌</span>
              </label>
              <p class="form-hint">
                開啟後，符合人數的座位已滿時可改派較大桌（包廂除外）並標示於訂位；關閉時則顯示已滿位。
              </p>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="maxDailyBookingMode">每日上限模式</label>
              <select
                id="maxDailyBookingMode"
                v-model="form.maxDailyBookingMode"
                class="form-control"
              >
                <option value="count">限制筆數（每筆訂位算 1）</option>
                <option value="guests">限制總人數（加總用餐人數）</option>
                <option value="tables">依照餐廳桌數</option>
              </select>
              <p v-if="form.maxDailyBookingMode === 'tables'" class="form-hint">
                當日已確認的訂位筆數不可超過此門市啟用中的桌數，不需再填每日最大預約數。
              </p>
            </div>

            <div v-if="form.maxDailyBookingMode !== 'tables'" class="form-group">
              <label for="maxDailyBookings">每日最大預約數</label>
              <input
                id="maxDailyBookings"
                v-model.number="form.maxDailyBookings"
                type="number"
                class="form-control"
                min="0"
                placeholder="留空代表不限制"
              />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="checkbox-label">
                <input
                  v-model="form.requireDeposit"
                  type="checkbox"
                  class="form-checkbox"
                />
                <span>需要訂金</span>
              </label>
            </div>
          </div>

          <div v-if="form.requireDeposit" class="form-row">
            <div class="form-group">
              <label for="depositAmount">訂金金額</label>
              <input
                id="depositAmount"
                v-model.number="form.depositAmount"
                type="number"
                class="form-control"
                :class="{ 'is-invalid': errors.depositAmount }"
                min="0"
                step="0.01"
              />
              <div v-if="errors.depositAmount" class="invalid-feedback">
                {{ errors.depositAmount }}
              </div>
            </div>

            <div class="form-group">
              <label for="cancellationHours">取消時限（小時）</label>
              <input
                id="cancellationHours"
                v-model.number="form.cancellationHours"
                type="number"
                class="form-control"
                min="0"
              />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="bookingNotice">訂位須知</label>
              <textarea
                id="bookingNotice"
                v-model="form.bookingNotice"
                class="form-control"
                rows="4"
                maxlength="2000"
                placeholder="例如：需提前一天取消、請準時入座、停車資訊"
              ></textarea>
              <p class="form-hint">顯示在顧客的訂位畫面。留空則不顯示。</p>
            </div>
          </div>

          <div class="notification-panel">
            <label class="checkbox-label notification-master">
              <input
                v-model="form.notifyCustomerOnNewReservation"
                type="checkbox"
                class="form-checkbox"
              />
              <span>消費者完成訂位時通知顧客</span>
            </label>
            <p class="form-hint">有 LINE 會員時發送 LINE；沒有 LINE 會員，或關閉 LINE 通知時，改發簡訊。兩種通知可分別開關。</p>
            <div
              class="notification-channel-list"
              :class="{ disabled: !form.notifyCustomerOnNewReservation }"
            >
              <div
                v-for="channel in confirmationNotificationChannels"
                :key="channel.value"
                class="notification-switch-row"
              >
                <label class="checkbox-label">
                  <input
                    v-model="channel.enabled"
                    type="checkbox"
                    class="form-checkbox"
                    :disabled="!form.notifyCustomerOnNewReservation"
                  />
                  <i :class="channel.icon"></i>
                  <span>{{ channel.label }}</span>
                </label>
                <p class="form-hint">{{ channel.hint }}</p>
              </div>
            </div>
            <div v-if="errors.notifyCustomerOnNewReservation" class="invalid-feedback d-block">
              {{ errors.notifyCustomerOnNewReservation }}
            </div>
          </div>

          <div class="notification-panel">
            <label class="checkbox-label notification-master">
              <input
                v-model="form.notifyWaitlistWhenTableReady"
                type="checkbox"
                class="form-checkbox"
              />
              <span>候位可入座時通知顧客</span>
            </label>
            <p class="form-hint">有 LINE 會員時發送 LINE；沒有 LINE 會員，或關閉 LINE 通知時，改發簡訊。自動語音電話之後才會提供。</p>
            <div
              class="notification-channel-list"
              :class="{ disabled: !form.notifyWaitlistWhenTableReady }"
            >
              <div
                v-for="channel in waitlistNotificationChannels"
                :key="channel.value"
                class="notification-switch-row"
              >
                <label class="checkbox-label">
                  <input
                    v-model="channel.enabled"
                    type="checkbox"
                    class="form-checkbox"
                    :disabled="!form.notifyWaitlistWhenTableReady"
                  />
                  <i :class="channel.icon"></i>
                  <span>{{ channel.label }}</span>
                </label>
                <p class="form-hint">{{ channel.hint }}</p>
              </div>
            </div>
            <div v-if="errors.notifyWaitlistWhenTableReady" class="invalid-feedback d-block">
              {{ errors.notifyWaitlistWhenTableReady }}
            </div>
          </div>

          <div class="notification-panel">
            <label class="checkbox-label notification-master">
              <input
                v-model="form.autoCancelNoShow"
                type="checkbox"
                class="form-checkbox"
              />
              <span>逾時未到自動取消</span>
            </label>
            <p class="form-hint">當天仍是「已確認」、超過預約時間還沒入座，會改為「未出席」並空出座位，再依上方候位通知設定通知下一位。已入座的不會取消。</p>
            <div class="form-group reminder-time-field">
              <label :class="{ 'required-label': form.autoCancelNoShow }">超過幾分鐘</label>
              <input
                v-model.number="form.noShowGraceMinutes"
                type="number"
                class="form-control"
                min="1"
                max="240"
                :disabled="!form.autoCancelNoShow"
              />
              <p class="form-hint">系統會定期檢查，開啟訂位列表時也會立刻檢查。</p>
            </div>
            <div v-if="errors.noShowGraceMinutes" class="invalid-feedback d-block">
              {{ errors.noShowGraceMinutes }}
            </div>
          </div>

          <div class="notification-panel">
            <label class="checkbox-label notification-master">
              <input
                v-model="form.notifyCustomerDayBefore"
                type="checkbox"
                class="form-checkbox"
              />
              <span>在訂位前一天通知顧客</span>
            </label>
            <div class="form-group reminder-time-field">
              <label
                for="customerReminderTime"
                :class="{ 'required-label': form.notifyCustomerDayBefore }"
              >發送時間</label>
              <select
                id="customerReminderTime"
                v-model="form.customerReminderTime"
                class="form-control"
                :disabled="!form.notifyCustomerDayBefore"
              >
                <option v-for="hour in reminderHours" :key="hour" :value="hour">{{ hour }}</option>
              </select>
              <p class="form-hint">每小時整點檢查一次，請選擇整點。</p>
            </div>
            <p class="form-hint">依下列順序嘗試通知，任一管道成功後即停止；未勾選的管道不會使用。</p>
            <div
              class="notification-channel-list"
              :class="{ disabled: !form.notifyCustomerDayBefore }"
            >
              <div
                v-for="(channel, index) in customerNotificationChannels"
                :key="channel.value"
                class="notification-channel-row"
              >
                <span class="channel-order">{{ index + 1 }}</span>
                <label class="checkbox-label">
                  <input
                    v-model="channel.enabled"
                    type="checkbox"
                    class="form-checkbox"
                    :disabled="!form.notifyCustomerDayBefore"
                  />
                  <i :class="channel.icon"></i>
                  <span>{{ channel.label }}</span>
                </label>
                <div class="channel-order-actions">
                  <button
                    type="button"
                    class="btn btn-sm btn-secondary"
                    :disabled="!form.notifyCustomerDayBefore || index === 0"
                    :aria-label="`${channel.label}往上移`"
                    @click="moveCustomerNotificationChannel(index, -1)"
                  >
                    <i class="ri-arrow-up-line"></i>
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-secondary"
                    :disabled="!form.notifyCustomerDayBefore || index === customerNotificationChannels.length - 1"
                    :aria-label="`${channel.label}往下移`"
                    @click="moveCustomerNotificationChannel(index, 1)"
                  >
                    <i class="ri-arrow-down-line"></i>
                  </button>
                </div>
              </div>
            </div>
            <div v-if="errors.notifyCustomerDayBefore" class="invalid-feedback d-block">
              {{ errors.notifyCustomerDayBefore }}
            </div>
          </div>
        </template>
        </div>

        <div v-if="activeTab === 'hours'" class="form-section">
          <h3 class="section-title">Step 3：設定門市營業時間</h3>
          <p class="form-hint">可在此直接管理每週營業時間，支援一天多個時段。顯示名稱會出現在顧客訂位頁的各時段，例如午餐、晚餐。未填時以該時段的起迄時間作為名稱。可把某一天的時段複製到平日、假日或全部星期，儲存後才會生效。</p>

          <div class="hours-table">
            <table>
              <thead>
                <tr>
                  <th>星期</th>
                  <th>是否營業</th>
                  <th>營業時段</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(day, dayIndex) in days" :key="day.value" :class="{ 'hours-row-invalid': dayHasError(dayIndex) }">
                  <td class="day-name">{{ day.label }}</td>
                  <td>
                    <label class="checkbox-label">
                      <input
                        v-model="hours[dayIndex].isOpen"
                        type="checkbox"
                        class="form-checkbox"
                        @change="handleDayToggle(dayIndex)"
                      />
                      <span>{{ hours[dayIndex].isOpen ? '營業' : '休息' }}</span>
                    </label>
                  </td>
                  <td>
                    <div v-if="hours[dayIndex].isOpen" class="slots-container">
                      <div
                        v-for="(slot, slotIndex) in hours[dayIndex].timeSlots"
                        :key="`${day.value}-${slotIndex}`"
                        class="slot-block"
                      >
                        <div class="slot-row">
                          <input
                            v-model="slot.displayName"
                            type="text"
                            class="form-control slot-name-input"
                            maxlength="20"
                            placeholder="顯示名稱"
                          />
                          <input
                            v-model="slot.openTime"
                            type="time"
                            class="form-control time-input"
                            :class="{ 'is-invalid': errors[`day${dayIndex}_slot${slotIndex}`] }"
                          />
                          <span class="slot-separator">~</span>
                          <input
                            v-model="slot.closeTime"
                            type="time"
                            class="form-control time-input"
                            :class="{ 'is-invalid': errors[`day${dayIndex}_slot${slotIndex}`] }"
                          />
                          <button
                            type="button"
                            class="btn btn-sm btn-danger"
                            :disabled="hours[dayIndex].timeSlots.length <= 1"
                            @click="removeSlot(dayIndex, slotIndex)"
                          >
                            刪除
                          </button>
                        </div>
                        <div v-if="errors[`day${dayIndex}_slot${slotIndex}`]" class="slot-error">
                          {{ errors[`day${dayIndex}_slot${slotIndex}`] }}
                        </div>
                      </div>
                      <div class="slot-actions">
                        <button type="button" class="btn btn-sm btn-secondary" @click="addSlot(dayIndex)">
                          <i class="ri-add-line"></i> 新增時段
                        </button>
                        <button type="button" class="btn btn-sm btn-secondary" @click="copyDaySlots(dayIndex, 'weekdays')">複製到平日</button>
                        <button type="button" class="btn btn-sm btn-secondary" @click="copyDaySlots(dayIndex, 'weekend')">複製到假日</button>
                        <button type="button" class="btn btn-sm btn-secondary" @click="copyDaySlots(dayIndex, 'all')">複製到全部</button>
                      </div>
                    </div>
                    <span v-else class="closed-text">休息日</span>
                    <div v-if="errors[`day${dayIndex}`]" class="slot-error">
                      {{ errors[`day${dayIndex}`] }}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="activeTab === 'closures'" class="form-section">
          <h3 class="section-title">特殊店休日設定</h3>
          <p class="form-hint">可設定單日或多日店休；整日店休會自動排除可訂位時段，並在訂位日曆上標示。</p>

          <div class="closure-form-card">
            <div class="form-row">
              <div class="form-group">
                <label class="required">店休名稱</label>
                <input v-model.trim="closureForm.name" type="text" class="form-control" maxlength="150" placeholder="例如：春節店休" />
              </div>
              <div class="form-group">
                <label class="required">模式</label>
                <select v-model="closureForm.dateMode" class="form-control" @change="handleClosureDateModeChange">
                  <option value="single">單日</option>
                  <option value="range">多日</option>
                </select>
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label class="required">{{ closureForm.dateMode === 'single' ? '店休日期' : '開始日期' }}</label>
                <input
                  v-model="closureForm.startDate"
                  type="date"
                  class="form-control"
                  @change="handleClosureStartDateChange"
                />
              </div>
              <div v-if="closureForm.dateMode === 'range'" class="form-group">
                <label class="required">結束日期</label>
                <input v-model="closureForm.endDate" type="date" class="form-control" />
              </div>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label class="checkbox-label">
                  <input v-model="closureForm.isAllDay" type="checkbox" class="form-checkbox" @change="handleClosureAllDayChange" />
                  <span>整日店休</span>
                </label>
              </div>
            </div>
            <div v-if="!closureForm.isAllDay" class="form-row">
              <div class="form-group">
                <label class="required">開始時間</label>
                <input v-model="closureForm.startTime" type="time" class="form-control" />
              </div>
              <div class="form-group">
                <label class="required">結束時間</label>
                <input v-model="closureForm.endTime" type="time" class="form-control" />
              </div>
            </div>
            <div class="form-row">
              <div class="form-group full-width">
                <label>原因說明</label>
                <textarea v-model.trim="closureForm.reason" class="form-control" rows="2" placeholder="選填"></textarea>
              </div>
            </div>
            <div class="closure-form-actions">
              <button
                v-if="closureForm.id"
                type="button"
                class="btn btn-secondary"
                :disabled="closureSaving"
                @click="resetClosureForm"
              >
                取消編輯
              </button>
              <button
                type="button"
                class="btn btn-primary"
                :disabled="closureSaving"
                @click="handleSaveClosure"
              >
                {{ closureSaving ? '儲存中...' : (closureForm.id ? '更新店休' : '新增店休') }}
              </button>
            </div>
          </div>

          <div v-if="closuresLoading" class="form-hint">載入店休日中...</div>
          <div v-else-if="closures.length === 0" class="form-hint">此門市尚未設定特殊店休日。</div>
          <div v-else class="closure-list">
            <div v-for="item in closures" :key="item.id" class="closure-item">
              <div class="closure-item-main">
                <strong>{{ item.name }}</strong>
                <span class="closure-item-meta">
                  {{ formatClosureRange(item) }}
                  ・{{ item.isAllDay ? '整日' : `${item.startTime}-${item.endTime}` }}
                </span>
                <span v-if="item.reason" class="closure-item-reason">{{ item.reason }}</span>
              </div>
              <div class="closure-item-actions">
                <button type="button" class="btn btn-sm btn-secondary" @click="startEditClosure(item)">編輯</button>
                <button type="button" class="btn btn-sm btn-danger" @click="handleDeleteClosure(item)">刪除</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="selectedStoreId && activeTab !== 'closures'" class="sync-panel">
        <div class="sync-panel-header">
          <h4 class="sync-panel-title">快速複製設定</h4>
          <p class="form-hint">可將目前畫面的訂位參數與營業時間快速套用到其他門市。</p>
        </div>

        <div class="sync-actions">
          <button
            type="button"
            class="btn btn-outline-primary btn-sm"
            :disabled="copying || stores.length <= 1"
            @click="handleApplyToAllStores"
          >
            套用到全部門市
          </button>
        </div>

        <div class="sync-target-section">
          <div class="sync-target-header">
            <span class="sync-target-title">套用到指定門市</span>
            <div class="sync-target-tools">
              <button type="button" class="btn-link" :disabled="copying" @click="selectAllTargetStores">
                全選
              </button>
              <button type="button" class="btn-link" :disabled="copying" @click="clearTargetStores">
                清除
              </button>
            </div>
          </div>

          <div v-if="otherStores.length === 0" class="form-hint">目前沒有可套用的其他門市。</div>
          <div v-else class="sync-target-list">
            <label v-for="store in otherStores" :key="`target-${store.id}`" class="sync-target-item">
              <input v-model="targetStoreIds" type="checkbox" :value="store.id" :disabled="copying" />
              <span>{{ store.storeName }}</span>
            </label>
          </div>

          <div class="sync-target-action">
            <button
              type="button"
              class="btn btn-secondary btn-sm"
              :disabled="copying || targetStoreIds.length === 0"
              @click="handleApplyToSelectedStores"
            >
              套用到選取門市（{{ targetStoreIds.length }}）
            </button>
          </div>
        </div>
      </div>
    </div>
    </div>
    </div>
  </div>
</template>

<script>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { storeService } from '@/api/modules/stores.js';
import { reservationService } from '@/api/modules/reservations.js';
import lineConfigApi from '@/api/modules/lineConfig';
import { clientService } from '@/api/modules/clients.js';
import authService from '@/helpers/authservice/authService.js';
import { closeSwal, showConfirm, showError, showLoading, showSuccess } from '@/utils/swal.js';

const createDefaultClosureForm = () => ({
  id: '',
  name: '',
  dateMode: 'single',
  startDate: '',
  endDate: '',
  isAllDay: true,
  startTime: '',
  endTime: '',
  reason: ''
});

const createDefaultForm = () => ({
  bookingEnabled: false,
  advanceBookingDays: 1,
  minAdvanceHours: 2,
  advanceBookingMode: 'hours',
  slotDuration: 60,
  limitDiningDuration: false,
  diningDurationMinutes: 90,
  allowUpwardTableAssignment: false,
  maxDailyBookings: null,
  maxDailyBookingMode: 'count',
  requireDeposit: false,
  depositAmount: null,
  cancellationHours: 24,
  bookingNotice: '',
  notifyCustomerOnNewReservation: false,
  notifyWaitlistWhenTableReady: false,
  autoCancelNoShow: false,
  noShowGraceMinutes: 15,
  notifyCustomerDayBefore: false,
  customerReminderTime: '18:00'
});
const days = [
  { label: '週日', value: 0 },
  { label: '週一', value: 1 },
  { label: '週二', value: 2 },
  { label: '週三', value: 3 },
  { label: '週四', value: 4 },
  { label: '週五', value: 5 },
  { label: '週六', value: 6 }
];
const reminderHours = Array.from({ length: 24 }, (_, hour) => (
  `${String(hour).padStart(2, '0')}:00`
));
const defaultConfirmationNotificationChannels = () => [
  {
    value: 'line',
    label: '開啟 LINE 通知',
    icon: 'ri-line-line',
    hint: '顧客是可接收推播的 LINE 會員時，發送訂位結果',
    enabled: true
  },
  {
    value: 'sms',
    label: '開啟簡訊通知',
    icon: 'ri-message-2-line',
    hint: '沒有 LINE 會員，或未開啟 LINE 通知時，以簡訊發送訂位結果',
    enabled: true
  }
];
const defaultWaitlistNotificationChannels = () => [
  {
    value: 'line',
    label: '開啟 LINE 通知',
    icon: 'ri-line-line',
    hint: '顧客是可接收推播的 LINE 會員時，通知可以入座',
    enabled: true
  },
  {
    value: 'sms',
    label: '開啟簡訊通知',
    icon: 'ri-message-2-line',
    hint: '沒有 LINE 會員，或未開啟 LINE 通知時，以簡訊通知可以入座',
    enabled: true
  }
];
const defaultCustomerNotificationChannels = () => [
  { value: 'line', label: 'LINE', icon: 'ri-line-line', enabled: true },
  { value: 'sms', label: '簡訊', icon: 'ri-message-2-line', enabled: true }
];
const normalizeReminderTime = (value) => {
  const hour = Number.parseInt(String(value || '18:00').slice(0, 2), 10);
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) return '18:00';
  return `${String(hour).padStart(2, '0')}:00`;
};
const createDefaultSlot = () => ({ displayName: '', openTime: '09:00', closeTime: '18:00' });
const createDefaultDay = (dayOfWeek) => ({
  dayOfWeek,
  isOpen: false,
  timeSlots: []
});
const createDefaultHours = () => Array.from({ length: 7 }, (_, i) => createDefaultDay(i));

export default {
  name: 'ReservationStoreSettings',
  setup() {
    const router = useRouter();
    const stores = ref([]);
    const selectedStoreId = ref('');
    const submitting = ref(false);
    const loading = ref(false);
    const errors = ref({});
    const activeTab = ref('settings');
    const isSelectingStore = ref(true);
    const copying = ref(false);
    const targetStoreIds = ref([]);
    const form = ref(createDefaultForm());
    const hours = ref(createDefaultHours());
    const closures = ref([]);
    const closuresLoading = ref(false);
    const closureSaving = ref(false);
    const closureForm = ref(createDefaultClosureForm());
    const confirmationNotificationChannels = ref(defaultConfirmationNotificationChannels());
    const waitlistNotificationChannels = ref(defaultWaitlistNotificationChannels());
    const customerNotificationChannels = ref(defaultCustomerNotificationChannels());
    const previousAdvanceBookingMode = ref(form.value.advanceBookingMode);
    const selectedStore = computed(() => stores.value.find(store => store.id === selectedStoreId.value) || null);
    const bookingLinksLoading = ref(false);
    const lineConfigs = ref([]);
    const clientMemberSystemType = ref('LOCKLINE');
    const copiedLinkKey = ref('');
    const liffPathPrefix = computed(() => (
      clientMemberSystemType.value === 'WIXTAR' ? '/wixtar' : '/lockline'
    ));

    const getLiffBaseUrl = () => {
      if (process.env.VUE_APP_LIFF_BASE_URL) {
        return process.env.VUE_APP_LIFF_BASE_URL;
      }
      const origin = window.location.origin;
      if (origin.includes('dev-admin.lockline.io')) return 'https://dev-liff.lockline.io';
      if (origin.includes('admin.lockline.io')) return 'https://liff.lockline.io';
      return 'https://liff.lockline.io';
    };

    const lineBookingUrl = (liffId, storeId = '') => {
      if (!liffId) return '';
      return `https://liff.line.me/${liffId}/reservations${storeId ? `/${storeId}` : ''}`;
    };

    const webBookingUrl = (channelId, storeId = '') => {
      if (!channelId) return '';
      const base = getLiffBaseUrl().replace(/\/$/, '');
      return `${base}${liffPathPrefix.value}/booking/${channelId}${storeId ? `/${storeId}` : ''}`;
    };

    const bookingLinkRows = computed(() => {
      const configs = lineConfigs.value.filter((config) => config.isActive !== false);
      const visibleConfigs = configs.length ? configs : lineConfigs.value;
      const store = selectedStore.value;
      const rows = [];

      visibleConfigs.forEach((config) => {
        const accountName = visibleConfigs.length > 1 ? `${config.configName}・` : '';
        rows.push({
          key: `line-all-${config.configId}`,
          label: `${accountName}全門市（LINE）`,
          url: lineBookingUrl(config.commonLiffId),
          missing: '請先到 LINE 設定填寫共用入口 LIFF ID'
        });
        rows.push({
          key: `web-all-${config.configId}`,
          label: `${accountName}全門市（一般瀏覽器）`,
          url: webBookingUrl(config.channelId),
          missing: '缺少頻道 ID'
        });
        if (store) {
          rows.push({
            key: `line-store-${config.configId}`,
            label: `${accountName}${store.storeName}（LINE）`,
            url: lineBookingUrl(config.commonLiffId, store.id),
            missing: '請先到 LINE 設定填寫共用入口 LIFF ID'
          });
          rows.push({
            key: `web-store-${config.configId}`,
            label: `${accountName}${store.storeName}（一般瀏覽器）`,
            url: webBookingUrl(config.channelId, store.id),
            missing: '缺少頻道 ID'
          });
        }
      });

      return rows;
    });

    const storeWaitlistUrl = computed(() => {
      const store = selectedStore.value;
      if (!store) return '';
      const configs = lineConfigs.value.filter((config) => config.isActive !== false);
      const config = (configs.length ? configs : lineConfigs.value)[0];
      if (!config?.commonLiffId) return '';
      return `https://liff.line.me/${config.commonLiffId}/waitlist/${store.id}`;
    });

    const loadBookingLinks = async () => {
      bookingLinksLoading.value = true;
      try {
        const user = authService.getCurrentUser();
        const clientTask = user?.clientId
          ? clientService.getClient(user.clientId).catch(() => null)
          : Promise.resolve(null);
        const [clientResponse, response] = await Promise.all([
          clientTask,
          lineConfigApi.list()
        ]);

        const memberSystemType = clientResponse?.data?.clientData?.memberSystemType;
        if (memberSystemType) {
          clientMemberSystemType.value = memberSystemType === 'WIXTAR' ? 'WIXTAR' : 'LOCKLINE';
        }

        const items = response?.data?.data?.items ?? response?.data?.items ?? [];
        lineConfigs.value = items.map((config) => {
          const liffIds = Array.isArray(config.liffIds) ? config.liffIds : [];
          return {
            configId: config.id,
            configName: config.configName || 'LINE 官方帳號',
            channelId: config.messagingApi?.channelId || config.messagingChannelId || '',
            commonLiffId: liffIds[0] || '',
            isActive: config.isActive
          };
        });
      } catch {
        lineConfigs.value = [];
      } finally {
        bookingLinksLoading.value = false;
      }
    };

    const copyBookingLink = async (link) => {
      if (!link?.url) return;
      try {
        await navigator.clipboard.writeText(link.url);
      } catch (err) {
        const input = document.createElement('textarea');
        input.value = link.url;
        input.setAttribute('readonly', '');
        input.style.position = 'fixed';
        input.style.left = '-9999px';
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
      }
      copiedLinkKey.value = link.key;
      setTimeout(() => {
        if (copiedLinkKey.value === link.key) copiedLinkKey.value = '';
      }, 2000);
    };
    const otherStores = computed(() => stores.value.filter(store => store.id !== selectedStoreId.value));

    const resetClosureForm = () => {
      closureForm.value = createDefaultClosureForm();
    };

    const formatClosureRange = (item) => {
      if (!item?.startDate) return '-';
      if (!item.endDate || item.endDate === item.startDate) return item.startDate;
      return `${item.startDate} ~ ${item.endDate}`;
    };

    const fetchClosures = async () => {
      if (!selectedStoreId.value) {
        closures.value = [];
        return;
      }
      closuresLoading.value = true;
      try {
        const response = await reservationService.getStoreClosures({
          storeId: selectedStoreId.value,
          status: 'active',
          page: 1,
          pageSize: 100
        });
        closures.value = response?.data?.items || [];
      } catch (err) {
        closures.value = [];
        showError(err.response?.data?.message || '載入店休日失敗');
      } finally {
        closuresLoading.value = false;
      }
    };

    const openClosuresTab = async () => {
      activeTab.value = 'closures';
      await fetchClosures();
    };

    const handleClosureDateModeChange = () => {
      if (closureForm.value.dateMode === 'single') {
        closureForm.value.endDate = closureForm.value.startDate;
      }
    };

    const handleClosureStartDateChange = () => {
      if (closureForm.value.dateMode === 'single') {
        closureForm.value.endDate = closureForm.value.startDate;
      } else if (
        closureForm.value.endDate
        && closureForm.value.endDate < closureForm.value.startDate
      ) {
        closureForm.value.endDate = closureForm.value.startDate;
      }
    };

    const handleClosureAllDayChange = () => {
      if (closureForm.value.isAllDay) {
        closureForm.value.startTime = '';
        closureForm.value.endTime = '';
      }
    };

    const startEditClosure = (item) => {
      closureForm.value = {
        id: item.id,
        name: item.name || '',
        dateMode: item.endDate && item.endDate !== item.startDate ? 'range' : 'single',
        startDate: item.startDate || '',
        endDate: item.endDate || item.startDate || '',
        isAllDay: item.isAllDay !== false,
        startTime: item.startTime || '',
        endTime: item.endTime || '',
        reason: item.reason || ''
      };
    };

    const handleSaveClosure = async () => {
      if (!selectedStoreId.value) {
        showError('請先選擇門市');
        return;
      }
      if (!closureForm.value.name?.trim()) {
        showError('請輸入店休名稱');
        return;
      }
      if (!closureForm.value.startDate) {
        showError('請選擇店休日期');
        return;
      }
      const endDate = closureForm.value.dateMode === 'single'
        ? closureForm.value.startDate
        : (closureForm.value.endDate || closureForm.value.startDate);
      if (endDate < closureForm.value.startDate) {
        showError('結束日期不可早於開始日期');
        return;
      }
      if (!closureForm.value.isAllDay) {
        if (!closureForm.value.startTime || !closureForm.value.endTime) {
          showError('非整日店休需設定起訖時間');
          return;
        }
        if (closureForm.value.startTime >= closureForm.value.endTime) {
          showError('結束時間需晚於開始時間');
          return;
        }
      }

      closureSaving.value = true;
      try {
        const payload = {
          storeId: selectedStoreId.value,
          name: closureForm.value.name.trim(),
          startDate: closureForm.value.startDate,
          endDate,
          isAllDay: Boolean(closureForm.value.isAllDay),
          startTime: closureForm.value.isAllDay ? undefined : closureForm.value.startTime,
          endTime: closureForm.value.isAllDay ? undefined : closureForm.value.endTime,
          reason: closureForm.value.reason || undefined
        };
        if (closureForm.value.id) {
          await reservationService.updateStoreClosure(closureForm.value.id, payload);
          showSuccess('店休日已更新');
        } else {
          await reservationService.createStoreClosure(payload);
          showSuccess('店休日已新增');
        }
        resetClosureForm();
        await fetchClosures();
      } catch (err) {
        showError(err.response?.data?.message || '儲存店休日失敗');
      } finally {
        closureSaving.value = false;
      }
    };

    const handleDeleteClosure = async (item) => {
      const confirmed = await showConfirm(
        '確定刪除此店休日？',
        `${item.name}（${formatClosureRange(item)}）`,
        '確定刪除',
        '取消',
        'warning'
      );
      if (!confirmed) return;
      try {
        await reservationService.deleteStoreClosure(item.id);
        showSuccess('店休日已刪除');
        if (closureForm.value.id === item.id) resetClosureForm();
        await fetchClosures();
      } catch (err) {
        showError(err.response?.data?.message || '刪除店休日失敗');
      }
    };

    const applyChannelSelection = (defaultsFactory, selected = []) => {
      const selectedValues = Array.isArray(selected) ? selected : [];
      const defaults = defaultsFactory();
      const byValue = new Map(defaults.map((item) => [item.value, item]));
      return [
        ...selectedValues.map((value) => ({ ...byValue.get(value), enabled: true })),
        ...defaults
          .filter((item) => !selectedValues.includes(item.value))
          .map((item) => ({ ...item, enabled: false }))
      ].filter((item) => item.value);
    };

    const setConfirmationNotificationChannels = (selected = []) => {
      confirmationNotificationChannels.value = applyChannelSelection(
        defaultConfirmationNotificationChannels,
        selected
      );
    };

    const setWaitlistNotificationChannels = (selected = []) => {
      waitlistNotificationChannels.value = applyChannelSelection(
        defaultWaitlistNotificationChannels,
        selected
      );
    };

    const setCustomerNotificationChannels = (selected = []) => {
      customerNotificationChannels.value = applyChannelSelection(
        defaultCustomerNotificationChannels,
        selected
      );
    };

    const moveChannelList = (listRef, index, offset) => {
      const target = index + offset;
      if (target < 0 || target >= listRef.value.length) return;
      const next = [...listRef.value];
      [next[index], next[target]] = [next[target], next[index]];
      listRef.value = next;
    };

    const moveCustomerNotificationChannel = (index, offset) => {
      moveChannelList(customerNotificationChannels, index, offset);
    };

    const normalizeForm = () => {
      if (form.value.advanceBookingMode === 'days' && (!form.value.advanceBookingDays || form.value.advanceBookingDays < 1)) {
        form.value.advanceBookingDays = 1;
      }

      if (!form.value.requireDeposit) {
        form.value.depositAmount = null;
      }
    };

    const fetchStores = async () => {
      try {
        const response = await storeService.getStores({ page: 1, pageSize: 200, sortBy: 'storeName', order: 'asc' });
        if (response.success && response.data?.items) {
          stores.value = response.data.items;
        }
      } catch (err) {
        showError('載入門市失敗');
      }
    };

    const fetchStoreSettings = async () => {
      if (!selectedStoreId.value) {
        form.value = createDefaultForm();
        hours.value = createDefaultHours();
        confirmationNotificationChannels.value = defaultConfirmationNotificationChannels();
        waitlistNotificationChannels.value = defaultWaitlistNotificationChannels();
        customerNotificationChannels.value = defaultCustomerNotificationChannels();
        previousAdvanceBookingMode.value = form.value.advanceBookingMode;
        return;
      }

      loading.value = true;
      try {
        const response = await storeService.getStore(selectedStoreId.value);
        if (!response.success || !response.data) {
          throw new Error('載入門市資料失敗');
        }

        const store = response.data;
        form.value = {
          bookingEnabled: Boolean(store.bookingEnabled),
          advanceBookingDays: store.advanceBookingDays ?? 1,
          minAdvanceHours: store.minAdvanceHours ?? 2,
          advanceBookingMode: store.advanceBookingMode || 'hours',
          slotDuration: store.slotDuration ?? 60,
          limitDiningDuration: Boolean(store.limitDiningDuration),
          diningDurationMinutes: store.diningDurationMinutes ?? 90,
          allowUpwardTableAssignment: Boolean(store.allowUpwardTableAssignment),
          maxDailyBookings: store.maxDailyBookings ?? null,
          maxDailyBookingMode: store.maxDailyBookingMode || 'count',
          requireDeposit: Boolean(store.requireDeposit),
          depositAmount: store.depositAmount ?? null,
          cancellationHours: store.cancellationHours ?? 24,
          bookingNotice: store.bookingNotice || '',
          notifyCustomerOnNewReservation: Boolean(store.notifyCustomerOnNewReservation),
          notifyWaitlistWhenTableReady: Boolean(store.notifyWaitlistWhenTableReady),
          autoCancelNoShow: Number.isInteger(store.noShowGraceMinutes) && store.noShowGraceMinutes > 0,
          noShowGraceMinutes: store.noShowGraceMinutes || 15,
          notifyCustomerDayBefore: Boolean(store.notifyCustomerDayBefore),
          customerReminderTime: normalizeReminderTime(store.customerReminderTime)
        };
        setConfirmationNotificationChannels(
          store.customerConfirmationNotificationChannels || ['line', 'sms']
        );
        setWaitlistNotificationChannels(store.waitlistNotificationChannels || ['line', 'sms']);
        setCustomerNotificationChannels(store.customerNotificationChannels || ['line', 'sms']);
        normalizeForm();
        previousAdvanceBookingMode.value = form.value.advanceBookingMode;

        const hoursResponse = await storeService.getStoreHours(selectedStoreId.value);
        const loadedHours = hoursResponse?.data?.hours || [];
        hours.value = createDefaultHours();
        for (let i = 0; i < 7; i++) {
          const existingHour = loadedHours.find(h => h.dayOfWeek === i);
          if (!existingHour) continue;

          let normalizedSlots = [];
          if (Array.isArray(existingHour.timeSlots) && existingHour.timeSlots.length > 0) {
            normalizedSlots = existingHour.timeSlots
              .filter(slot => slot && slot.openTime && slot.closeTime)
              .map(slot => ({
                displayName: slot.displayName || '',
                openTime: slot.openTime,
                closeTime: slot.closeTime
              }));
          } else if (existingHour.openTime && existingHour.closeTime) {
            if (existingHour.breakStart && existingHour.breakEnd) {
              normalizedSlots = [
                { openTime: existingHour.openTime, closeTime: existingHour.breakStart },
                { openTime: existingHour.breakEnd, closeTime: existingHour.closeTime }
              ];
            } else {
              normalizedSlots = [{ openTime: existingHour.openTime, closeTime: existingHour.closeTime }];
            }
          }

          hours.value[i] = {
            dayOfWeek: i,
            isOpen: existingHour.isOpen || normalizedSlots.length > 0,
            timeSlots: normalizedSlots.length > 0 ? normalizedSlots : (existingHour.isOpen ? [createDefaultSlot()] : [])
          };
        }
      } catch (err) {
        showError(err.message || '載入門市訂位參數失敗');
      } finally {
        loading.value = false;
      }
    };

    const handleStoreChange = async () => {
      errors.value = {};
      await fetchStoreSettings();
    };
    const selectStore = async (storeId) => {
      if (!storeId || selectedStoreId.value === storeId) return;
      selectedStoreId.value = storeId;
      await handleStoreChange();
      activeTab.value = 'settings';
      resetClosureForm();
      closures.value = [];
      isSelectingStore.value = false;
    };
    const startStoreSelection = () => {
      isSelectingStore.value = true;
    };
    const getStoreImage = (store) => {
      if (!store || !Array.isArray(store.storeImages)) return '';
      return store.storeImages.find((url) => typeof url === 'string' && url.trim()) || '';
    };
    const formatStoreAddress = (store) => {
      if (!store) return '未提供地址';
      const parts = [store.city, store.district, store.street]
        .filter((part) => typeof part === 'string' && part.trim())
        .map((part) => part.trim());
      return parts.length > 0 ? parts.join('') : '未提供地址';
    };

    const handleAdvanceBookingModeChange = () => {
      if (form.value.advanceBookingMode === 'days' && previousAdvanceBookingMode.value !== 'days') {
        form.value.advanceBookingDays = 1;
      }
      normalizeForm();
      previousAdvanceBookingMode.value = form.value.advanceBookingMode;
    };

    const handleDayToggle = (dayIndex) => {
      if (!hours.value[dayIndex].isOpen) {
        hours.value[dayIndex].timeSlots = [];
      } else if (!hours.value[dayIndex].timeSlots.length) {
        hours.value[dayIndex].timeSlots = [createDefaultSlot()];
      }
    };

    const addSlot = (dayIndex) => {
      hours.value[dayIndex].timeSlots.push(createDefaultSlot());
    };

    const copyDaySlots = (sourceIndex, mode) => {
      const source = hours.value[sourceIndex];
      const targets = mode === 'weekdays' ? [1, 2, 3, 4, 5]
        : mode === 'weekend' ? [0, 6] : [0, 1, 2, 3, 4, 5, 6];
      targets.forEach((index) => {
        if (index === sourceIndex) return;
        hours.value[index].isOpen = source.isOpen;
        hours.value[index].timeSlots = source.isOpen
          ? source.timeSlots.map((slot) => ({
            displayName: slot.displayName || '',
            openTime: slot.openTime,
            closeTime: slot.closeTime
          }))
          : [];
      });
    };

    const dayHasError = (dayIndex) => Object.keys(errors.value).some((key) => key === `day${dayIndex}` || key.startsWith(`day${dayIndex}_slot`));

    const removeSlot = (dayIndex, slotIndex) => {
      if (hours.value[dayIndex].timeSlots.length <= 1) return;
      hours.value[dayIndex].timeSlots.splice(slotIndex, 1);
    };

    const validateHours = () => {
      for (let i = 0; i < hours.value.length; i++) {
        const day = hours.value[i];
        if (!day.isOpen) continue;

        if (!Array.isArray(day.timeSlots) || day.timeSlots.length === 0) {
          errors.value[`day${i}`] = '請至少新增一個營業時段';
          continue;
        }

        const sortedSlots = [...day.timeSlots]
          .map((slot, idx) => ({ ...slot, idx }))
          .sort((a, b) => a.openTime.localeCompare(b.openTime));

        sortedSlots.forEach((slot, sortedIdx) => {
          if (!slot.openTime || !slot.closeTime) {
            errors.value[`day${i}_slot${slot.idx}`] = '請輸入完整開始與結束時間';
            return;
          }
          if (slot.openTime >= slot.closeTime) {
            errors.value[`day${i}_slot${slot.idx}`] = '結束時間必須晚於開始時間';
            return;
          }
          if (sortedIdx > 0 && slot.openTime < sortedSlots[sortedIdx - 1].closeTime) {
            errors.value[`day${i}_slot${slot.idx}`] = '時段不可重疊';
          }
        });
      }
    };
    const validateSettings = () => {
      if (form.value.requireDeposit) {
        const depositAmount = Number(form.value.depositAmount);
        if (!Number.isFinite(depositAmount) || depositAmount <= 0) {
          errors.value.depositAmount = '勾選需要訂金時，訂金金額必須大於 0';
        }
      }
      if (form.value.limitDiningDuration) {
        const diningMinutes = Number(form.value.diningDurationMinutes);
        if (!Number.isInteger(diningMinutes) || diningMinutes < 1) {
          errors.value.diningDurationMinutes = '啟用限定用餐時間時，請輸入大於 0 的分鐘數';
        }
      }
      if (form.value.notifyCustomerOnNewReservation
        && !confirmationNotificationChannels.value.some((channel) => channel.enabled)) {
        errors.value.notifyCustomerOnNewReservation = '啟用訂位完成通知時，請至少勾選一個通知管道';
      }
      if (form.value.notifyWaitlistWhenTableReady
        && !waitlistNotificationChannels.value.some((channel) => channel.enabled)) {
        errors.value.notifyWaitlistWhenTableReady = '啟用候位通知時，請至少勾選一個通知管道';
      }
      if (form.value.autoCancelNoShow) {
        const graceMinutes = Number(form.value.noShowGraceMinutes);
        if (!Number.isInteger(graceMinutes) || graceMinutes < 1 || graceMinutes > 240) {
          errors.value.noShowGraceMinutes = '請輸入 1 到 240 的分鐘數';
        }
      }
      if (form.value.notifyCustomerDayBefore) {
        if (!form.value.customerReminderTime) {
          errors.value.notifyCustomerDayBefore = '啟用顧客前一天提醒時，請設定發送時間';
        } else if (!customerNotificationChannels.value.some((channel) => channel.enabled)) {
          errors.value.notifyCustomerDayBefore = '啟用顧客前一天提醒時，請至少勾選一個通知管道';
        }
      }
    };

    const buildHoursPayload = () => hours.value
      .filter(day => day.isOpen)
      .map(day => ({
        dayOfWeek: day.dayOfWeek,
        isOpen: true,
        timeSlots: [...day.timeSlots]
          .sort((a, b) => a.openTime.localeCompare(b.openTime))
          .map(slot => ({
            displayName: String(slot.displayName || '').trim(),
            openTime: slot.openTime,
            closeTime: slot.closeTime
          }))
      }));
    const buildStoreSettingsPayload = () => ({
      bookingEnabled: form.value.bookingEnabled,
      advanceBookingDays: form.value.advanceBookingDays,
      minAdvanceHours: form.value.minAdvanceHours,
      advanceBookingMode: form.value.advanceBookingMode,
      slotDuration: form.value.slotDuration,
      limitDiningDuration: Boolean(form.value.limitDiningDuration),
      diningDurationMinutes: form.value.limitDiningDuration
        ? Number(form.value.diningDurationMinutes)
        : null,
      allowUpwardTableAssignment: Boolean(form.value.allowUpwardTableAssignment),
      maxDailyBookings: form.value.maxDailyBookings,
      maxDailyBookingMode: form.value.maxDailyBookingMode,
      requireDeposit: form.value.requireDeposit,
      depositAmount: form.value.depositAmount,
      cancellationHours: form.value.cancellationHours,
      bookingNotice: form.value.bookingNotice?.trim() || null,
      notifyCustomerOnNewReservation: form.value.notifyCustomerOnNewReservation,
      customerConfirmationNotificationChannels: confirmationNotificationChannels.value
        .filter((channel) => channel.enabled)
        .map((channel) => channel.value),
      notifyWaitlistWhenTableReady: form.value.notifyWaitlistWhenTableReady,
      waitlistNotificationChannels: waitlistNotificationChannels.value
        .filter((channel) => channel.enabled)
        .map((channel) => channel.value),
      noShowGraceMinutes: form.value.autoCancelNoShow
        ? Number(form.value.noShowGraceMinutes)
        : null,
      notifyCustomerDayBefore: form.value.notifyCustomerDayBefore,
      customerReminderTime: normalizeReminderTime(form.value.customerReminderTime),
      customerNotificationChannels: customerNotificationChannels.value
        .filter((channel) => channel.enabled)
        .map((channel) => channel.value)
    });
    const ensureSettingsReady = async () => {
      errors.value = {};
      normalizeForm();
      validateSettings();
      validateHours();
      const errorMessages = Object.values(errors.value);
      if (errorMessages.length > 0) {
        if (Object.keys(errors.value).some((key) => key.startsWith('day'))) {
          activeTab.value = 'hours';
          await nextTick();
          document.querySelector('.hours-table .slot-error')?.scrollIntoView({ block: 'center' });
        }
        await showError(errorMessages[0]);
        return false;
      }
      return true;
    };
    const applySettingsToStore = async (storeId, payload, hoursPayload) => {
      await storeService.updateStore(storeId, payload);
      await storeService.setStoreHours(storeId, hoursPayload);
    };
    const selectAllTargetStores = () => {
      targetStoreIds.value = otherStores.value.map(store => store.id);
    };
    const clearTargetStores = () => {
      targetStoreIds.value = [];
    };
    const handleApplyToSelectedStores = async () => {
      if (!selectedStoreId.value || targetStoreIds.value.length === 0) return;
      const ready = await ensureSettingsReady();
      if (!ready) return;

      const confirmed = await showConfirm(
        '套用到選取門市？',
        `將套用到 ${targetStoreIds.value.length} 間門市（含訂位參數與營業時間）`,
        '確認套用'
      );
      if (!confirmed) return;

      copying.value = true;
      showLoading('批次套用中...');
      try {
        const payload = buildStoreSettingsPayload();
        const hoursPayload = buildHoursPayload();
        for (const storeId of targetStoreIds.value) {
          await applySettingsToStore(storeId, payload, hoursPayload);
        }
        closeSwal();
        await showSuccess('批次套用完成', `已更新 ${targetStoreIds.value.length} 間門市`);
      } catch (err) {
        closeSwal();
        const message = err.response?.data?.message || '批次套用失敗';
        await showError('批次套用失敗', message);
      } finally {
        copying.value = false;
      }
    };
    const handleApplyToAllStores = async () => {
      if (!selectedStoreId.value || stores.value.length === 0) return;
      const ready = await ensureSettingsReady();
      if (!ready) return;

      const confirmed = await showConfirm(
        '套用到全部門市？',
        `將套用到全部 ${stores.value.length} 間門市（含目前門市）`,
        '確認套用'
      );
      if (!confirmed) return;

      copying.value = true;
      showLoading('批次套用中...');
      try {
        const payload = buildStoreSettingsPayload();
        const hoursPayload = buildHoursPayload();
        for (const store of stores.value) {
          await applySettingsToStore(store.id, payload, hoursPayload);
        }
        closeSwal();
        await showSuccess('全部套用完成', `已更新 ${stores.value.length} 間門市`);
      } catch (err) {
        closeSwal();
        const message = err.response?.data?.message || '全部套用失敗';
        await showError('全部套用失敗', message);
      } finally {
        copying.value = false;
      }
    };

    const handleSubmit = async () => {
      errors.value = {};
      if (!selectedStoreId.value) {
        errors.value.storeId = '請先選擇門市';
        return;
      }

      const ready = await ensureSettingsReady();
      if (!ready) return;
      submitting.value = true;
      try {
        const payload = buildStoreSettingsPayload();
        const hoursPayload = buildHoursPayload();
        await applySettingsToStore(selectedStoreId.value, payload, hoursPayload);
        showSuccess('訂位參數與營業時間已更新');
      } catch (err) {
        const message = err.response?.data?.message || '更新訂位參數失敗';
        showError(message);
      } finally {
        submitting.value = false;
      }
    };

    const handleBack = () => {
      router.push({ name: 'ClientReservations' });
    };

    onMounted(async () => {
      await Promise.all([fetchStores(), loadBookingLinks()]);
    });
    watch(selectedStoreId, () => {
      targetStoreIds.value = targetStoreIds.value.filter(storeId => storeId !== selectedStoreId.value);
    });

    return {
      stores,
      otherStores,
      days,
      reminderHours,
      hours,
      selectedStoreId,
      selectedStore,
      form,
      confirmationNotificationChannels,
      waitlistNotificationChannels,
      customerNotificationChannels,
      loading,
      submitting,
      copying,
      errors,
      activeTab,
      isSelectingStore,
      targetStoreIds,
      closures,
      closuresLoading,
      closureSaving,
      closureForm,
      selectStore,
      startStoreSelection,
      selectAllTargetStores,
      clearTargetStores,
      handleApplyToSelectedStores,
      handleApplyToAllStores,
      getStoreImage,
      formatStoreAddress,
      handleAdvanceBookingModeChange,
      handleDayToggle,
      addSlot,
      dayHasError,
      copyDaySlots,
      removeSlot,
      moveCustomerNotificationChannel,
      openClosuresTab,
      resetClosureForm,
      formatClosureRange,
      handleClosureDateModeChange,
      handleClosureStartDateChange,
      handleClosureAllDayChange,
      startEditClosure,
      handleSaveClosure,
      handleDeleteClosure,
      handleSubmit,
      handleBack,
      bookingLinksLoading,
      bookingLinkRows,
      storeWaitlistUrl,
      copiedLinkKey,
      copyBookingLink
    };
  }
};
</script>

<style scoped>
.reservation-store-settings-container {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.form-container {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.form-section {
  margin-bottom: 28px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e5e7eb;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.required::after {
  content: ' *';
  color: #ef4444;
}

.form-hint {
  margin-top: 6px;
  font-size: 12px;
  color: #6b7280;
}

.checkbox-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
}

.booking-enable-card {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 20px;
  padding: 16px 18px;
  border: 2px solid #d1d5db;
  border-radius: 12px;
  background: #f3f4f6;
  cursor: pointer;
}

.booking-enable-card.is-on {
  border-color: #16a34a;
  background: #f0fdf4;
}

.booking-enable-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.booking-enable-title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  line-height: 1.3;
}

.booking-enable-status {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
}

.booking-enable-card.is-on .booking-enable-status {
  color: #166534;
}

.booking-enable-switch {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.booking-enable-switch input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.booking-enable-slider {
  position: relative;
  width: 56px;
  height: 32px;
  border-radius: 999px;
  background: #9ca3af;
  transition: background 0.2s ease;
}

.booking-enable-slider::before {
  content: '';
  position: absolute;
  top: 3px;
  left: 3px;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  transition: transform 0.2s ease;
}

.booking-enable-switch input:checked + .booking-enable-slider {
  background: #16a34a;
}

.booking-enable-switch input:checked + .booking-enable-slider::before {
  transform: translateX(24px);
}

.booking-enable-switch input:focus-visible + .booking-enable-slider {
  outline: 2px solid #409eff;
  outline-offset: 2px;
}

.booking-enable-state {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  background: #e5e7eb;
  font-size: 13px;
  font-weight: 700;
  color: #4b5563;
  line-height: 1.4;
}

.booking-enable-card.is-on .booking-enable-state {
  background: #16a34a;
  color: #fff;
}

@media (max-width: 640px) {
  .booking-enable-card {
    align-items: flex-start;
    flex-direction: column;
  }
}

.store-picker {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 12px;
}

.store-picker-btn {
  border: 1px solid #dbe1ea;
  background: #fff;
  color: #111827;
  border-radius: 10px;
  padding: 0;
  text-align: left;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
}

.store-picker-btn:hover {
  border-color: #409eff;
  box-shadow: 0 8px 20px rgba(64, 158, 255, 0.12);
}

.store-picker-btn.is-selected {
  border-color: #409eff;
  background: #f6faff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}

.store-picker-btn.is-booking-enabled {
  border-color: #16a34a;
  background: #f0fdf4;
}

.store-picker-btn.is-booking-enabled:hover {
  border-color: #15803d;
  box-shadow: 0 8px 20px rgba(22, 163, 74, 0.18);
}

.store-picker-btn.is-booking-enabled.is-selected {
  border-color: #15803d;
  background: #dcfce7;
  box-shadow: 0 0 0 2px rgba(22, 163, 74, 0.28);
}

.store-card-name-row,
.selected-store-name-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.booking-badge {
  flex-shrink: 0;
  background: #16a34a;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  padding: 4px 6px;
  border-radius: 999px;
}

.store-card-image-wrapper {
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #f3f4f6;
}

.store-card-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.store-card-image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 28px;
}

.store-card-content {
  padding: 10px 12px 12px;
}

.store-card-name {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 4px;
}

.store-card-address {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
  min-height: 18px;
}

.selected-store-compact {
  display: flex;
  align-items: center;
  gap: 12px;
}

.selected-store-card {
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #dbe1ea;
  border-radius: 10px;
  padding: 8px 10px;
  background: #f8fbff;
  max-width: 520px;
}

.selected-store-card.is-booking-enabled {
  border-color: #16a34a;
  background: #f0fdf4;
}

.selected-store-image-wrapper {
  width: 56px;
  height: 56px;
  border-radius: 8px;
  overflow: hidden;
  background: #f3f4f6;
  flex-shrink: 0;
}

.selected-store-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.selected-store-image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 20px;
}

.selected-store-content {
  min-width: 0;
}

.selected-store-name {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.selected-store-address {
  margin-top: 2px;
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tab-panel {
  margin-bottom: 28px;
}

.tabs-nav {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 14px;
  padding: 0 8px;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  border-bottom: 1px solid #e5e7eb;
}

.tabs-nav-item {
  border: none;
  border-radius: 0;
  background: transparent;
  color: #6b7280;
  padding: 10px 2px;
  margin-bottom: -1px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s ease, border-color 0.2s ease;
  border-bottom: 2px solid transparent;
}

.tabs-nav-item:hover {
  color: #2563eb;
}

.tabs-nav-item.active {
  color: #1d4ed8;
  border-bottom-color: #409eff;
  font-weight: 600;
}

.sync-panel {
  margin-bottom: 18px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px;
  background: #fafcff;
}

.sync-panel-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
}

.sync-actions {
  margin-top: 8px;
  display: flex;
  gap: 8px;
}

.sync-target-section {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #dbe2ef;
}

.sync-target-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.sync-target-title {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.sync-target-tools {
  display: flex;
  gap: 10px;
}

.btn-link {
  border: none;
  background: transparent;
  padding: 0;
  font-size: 12px;
  color: #2563eb;
  cursor: pointer;
}

.btn-link:disabled {
  color: #9ca3af;
  cursor: not-allowed;
}

.sync-target-list {
  margin-top: 8px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 8px 12px;
  max-height: 140px;
  overflow-y: auto;
}

.sync-target-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #374151;
}

.sync-target-action {
  margin-top: 10px;
}

.btn-outline-primary {
  border: 1px solid #409eff;
  color: #1d4ed8;
  background: #fff;
}

.btn-outline-primary:hover:not(:disabled) {
  background: #ecf5ff;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
}

.notification-panel {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  margin-top: 20px;
  padding: 16px;
}

.notification-master {
  font-weight: 600;
}

.reminder-time-field {
  margin: 14px 0 0;
  max-width: 220px;
}

.required-label::after {
  content: ' *';
  color: #dc3545;
}

.notification-channel-list {
  display: grid;
  gap: 8px;
  margin-top: 14px;
  max-width: 520px;
}

.notification-channel-list.disabled {
  opacity: 0.65;
}

.notification-channel-row,
.notification-switch-row {
  align-items: center;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  display: grid;
  gap: 12px;
  padding: 9px 10px;
}

.notification-channel-row {
  grid-template-columns: 28px 1fr auto;
}

.notification-switch-row {
  grid-template-columns: 1fr;
  align-items: start;
}

.notification-switch-row .form-hint {
  margin: 0 0 0 28px;
}

.channel-order {
  align-items: center;
  background: #eef2ff;
  border-radius: 50%;
  color: #4338ca;
  display: inline-flex;
  font-size: 12px;
  font-weight: 700;
  height: 24px;
  justify-content: center;
  width: 24px;
}

.channel-order-actions {
  display: flex;
  gap: 5px;
}

.d-block {
  display: block;
  margin-top: 8px;
}

.hours-table {
  overflow-x: auto;
  margin-top: 12px;
}

.hours-table table {
  width: 100%;
  border-collapse: collapse;
  min-width: 760px;
}

.hours-table th,
.hours-table td {
  padding: 10px;
  border-bottom: 1px solid #e5e7eb;
  text-align: left;
  vertical-align: top;
}

.hours-table th {
  background: #f9fafb;
  color: #374151;
  font-weight: 600;
}

.day-name {
  min-width: 60px;
  font-weight: 500;
}

.slots-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.slot-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.slot-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.hours-row-invalid {
  background: #fef2f2;
}

.time-input.is-invalid {
  border-color: #dc2626;
  background-color: #fff1f2;
  box-shadow: 0 0 0 1px #dc2626;
}

.slot-error {
  color: #dc2626;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;
}

.slot-name-input {
  width: 120px;
  flex: 0 0 120px;
}

.time-input {
  min-width: 120px;
}

.slot-separator {
  color: #6b7280;
}

.slot-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.btn-sm {
  padding: 6px 10px;
  font-size: 12px;
}

.btn-danger {
  background-color: #ef4444;
  color: #fff;
  white-space: nowrap;
  flex-shrink: 0;
}

.closed-text {
  color: #9ca3af;
}

.closure-form-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  margin-bottom: 20px;
  padding: 16px;
}

.closure-form-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 8px;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.booking-link-list {
  display: grid;
  gap: 10px;
  margin-top: 12px;
}

.booking-link-row {
  align-items: center;
  background: #fff;
  border: 1px solid #e7e5e4;
  border-radius: 10px;
  display: grid;
  gap: 8px 12px;
  grid-template-columns: 220px minmax(0, 1fr) auto;
  padding: 10px 12px;
}

.booking-link-label {
  color: #44403c;
  font-size: 14px;
  font-weight: 600;
}

.booking-link-value {
  min-width: 0;
}

.booking-link-value a,
.booking-link-missing {
  display: block;
  overflow-wrap: anywhere;
  font-size: 13px;
}

.booking-link-missing {
  color: #a8a29e;
}

@media (max-width: 768px) {
  .booking-link-row {
    grid-template-columns: 1fr;
  }
}

.closure-list {
  display: grid;
  gap: 10px;
}

.closure-item {
  align-items: center;
  background: #fff;
  border: 1px solid #fecaca;
  border-radius: 10px;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  padding: 12px 14px;
}

.closure-item-main {
  display: grid;
  gap: 4px;
}

.closure-item-meta {
  color: #64748b;
  font-size: 13px;
}

.closure-item-reason {
  color: #475569;
  font-size: 13px;
}

.closure-item-actions {
  display: flex;
  flex-shrink: 0;
  gap: 8px;
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }

  .closure-item {
    align-items: stretch;
    flex-direction: column;
  }

  .selected-store-compact {
    flex-direction: column;
    align-items: stretch;
  }

  .selected-store-card {
    max-width: none;
  }
}
</style>
